<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    import { tweened } from 'svelte/motion';
    import { cubicOut } from 'svelte/easing';

    const skins = {
        primary: 'primary',
        secondary: 'secondary',
        'secondary-thin': 'secondary-thin'
    };

    export let max = 1;
    export let value = 0;
    export let valueLabel;
    export let label;
    export let showLabel = true;
    export let skin = skins.primary;

    if (max <= 0) {
        max = 1;
    }

    if (!skins[skin]) {
        skin = skins.primary;
    }

    // Use a tweened store to animate the value:
    let tweenedValue = tweened(0, {
        duration: 300,
        easing: cubicOut
    });
    $: tweenedValue.set(Math.min(max, value));

    const getPercentage = val => `${Math.ceil((val / max) * 100)}%`;
    $: tweenedPercentage = getPercentage($tweenedValue);
    $: percentage = getPercentage(value);
</script>

<style>
    [role='progressbar'] {
        /* primary */
        --skin-primary-bar-height: 3rem;

        /* secondary */
        --skin-secondary-bar-height: 3rem;

        /* secondary-thin */
        --skin-secondary-thin-bar-height: 1.5rem;

        display: block;
        width: 100%;
        text-align: center;
        position: relative;

        & .track,
        & span {
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            position: absolute;
        }
        & .track {
            width: var(--progress-value-width, 0);
        }

        /** primary **/
        &.primary {
            background: var(--color-bg-inverted);
            height: var(--skin-primary-bar-height);
        }
        &.primary .track {
            background: var(--color-brand);
        }
        &.primary span {
            color: var(--color-text-inverted);
            width: 100%;
            font-size: var(--fontsize-body-xs);
            line-height: var(--skin-primary-bar-height);
        }

        /** secondary **/
        &.secondary {
            background: var(--color-bg-default);
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: calc(var(--radius-large) + var(--border-medium));
            height: var(--skin-secondary-bar-height);
        }
        &.secondary .track {
            background: var(--color-bg-inverted);
            border-radius: var(--radius-large);
        }
        &.secondary span {
            display: none;
        }

        /** secondary-thin **/
        &.secondary-thin {
            background: var(--color-bg-default);
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: calc(var(--radius-large) + var(--border-medium));
            height: var(--skin-secondary-thin-bar-height);
        }
        &.secondary-thin .track {
            background: var(--color-bg-inverted);
            border-radius: var(--radius-large);
        }
        &.secondary-thin span {
            display: none;
        }
    }
</style>

<div
    role="progressbar"
    class={skin || skins.primary}
    aria-valuemin={0}
    aria-valuemax={max}
    aria-valuenow={value}
    aria-valuetext={valueLabel || percentage}>
    <div class="track" style="--progress-value-width: {tweenedPercentage}" />
    {#if showLabel}
        <span>{label || valueLabel || percentage}</span>
    {/if}
</div>
